import React, { useState, useMemo } from 'react';
import { useSettings } from './SettingsContext';
import { DunningRule, DunningStep, CancellationSaverRule } from '../types';
import DunningFlowchart from './DunningFlowchart';
import CancellationSaver from './CancellationSaver';

const DunningManagement: React.FC = () => {
  const { settings, updateSettings } = useSettings();
  const [showSaved, setShowSaved] = useState(false);
  const [showUndo, setShowUndo] = useState(false);
  const [lastDeletedStep, setLastDeletedStep] = useState<{ step: DunningStep; index: number } | null>(null);
  const [viewMode, setViewMode] = useState<'timeline' | 'flowchart'>('flowchart');
  const [activeTab, setActiveTab] = useState<'dunning' | 'cancellationSaver'>('dunning');

  // Focus on the primary dunning rule
  const rule = settings.dunningRules[0] || {
    id: 'dr-default',
    name: 'Standard Recovery Policy',
    isEnabled: true,
    gracePeriodDays: 3,
    maxDunningDays: 7,
    steps: []
  };

  const cancellationSaverRule = settings.cancellationSaverRules[0] || {
    id: 'cs-default',
    name: 'Standard Cancellation Saver',
    isEnabled: true,
    offerType: 'discount',
    discountValue: 10,
    pauseMonths: 1
  };

  const handleRuleUpdate = (updates: Partial<DunningRule>) => {
    const updatedRules = settings.dunningRules.length > 0 
      ? settings.dunningRules.map(r => r.id === rule.id ? { ...r, ...updates } : r)
      : [{ ...rule, ...updates }];
      
    updateSettings({ dunningRules: updatedRules });
    setShowSaved(true);
    setTimeout(() => setShowSaved(false), 2000);
  };

  const handleCancellationSaverUpdate = (updates: Partial<CancellationSaverRule>) => {
    const updatedRules = settings.cancellationSaverRules.length > 0 
      ? settings.cancellationSaverRules.map(r => r.id === cancellationSaverRule.id ? { ...r, ...updates } : r)
      : [{ ...cancellationSaverRule, ...updates }];
      
    updateSettings({ cancellationSaverRules: updatedRules });
    setShowSaved(true);
    setTimeout(() => setShowSaved(false), 2000);
  };

  const updateStep = (stepId: string, updates: Partial<DunningStep>) => {
    const updatedSteps = rule.steps.map(s => 
      s.id === stepId ? { ...s, ...updates } : s
    );
    handleRuleUpdate({ steps: updatedSteps });
  };

  const addStep = () => {
    const newStep: DunningStep = { 
      id: 's' + Date.now(), 
      dayDelay: 3, 
      emailTemplateId: 'none', 
      action: 'retry' 
    };
    handleRuleUpdate({ steps: [...rule.steps, newStep] });
  };

  const deleteStep = (id: string) => {
    const index = rule.steps.findIndex(s => s.id === id);
    if (index !== -1) {
      const stepToDelete = rule.steps[index];
      setLastDeletedStep({ step: stepToDelete, index });
      handleRuleUpdate({ steps: rule.steps.filter(s => s.id !== id) });
      setShowUndo(true);
      setTimeout(() => setShowUndo(false), 5000);
    }
  };

  const undoDelete = () => {
    if (lastDeletedStep) {
      const newSteps = [...rule.steps];
      newSteps.splice(lastDeletedStep.index, 0, lastDeletedStep.step);
      handleRuleUpdate({ steps: newSteps });
      setLastDeletedStep(null);
      setShowUndo(false);
    }
  };

  const totalDunningDays = useMemo(() => rule.steps.reduce((acc, s) => acc + s.dayDelay, 0), [rule.steps]);

  const getActionColor = (action: string) => {
    switch (action) {
      case 'retry': return 'text-indigo-600 bg-indigo-50 border-indigo-100';
      case 'mark_past_due': return 'text-amber-600 bg-amber-50 border-amber-100';
      case 'cancel': return 'text-red-600 bg-red-50 border-red-100';
      default: return 'text-slate-600 bg-slate-50 border-slate-100';
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'retry': 
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M8 16H3v5"/></svg>
        );
      case 'mark_past_due': 
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        );
      case 'cancel': 
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
        );
      default: return null;
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-10 animate-in fade-in duration-500 pb-32">
      {/* Dynamic Header */}
      <div className="flex flex-col md:flex-row justify-between items-end gap-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-indigo-600 rounded-[1.5rem] flex items-center justify-center text-white shadow-2xl shadow-indigo-200 dark:shadow-none transition-transform hover:rotate-3">
             <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m3 11 18-5v12L3 14v-3z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/></svg>
          </div>
          <div>
            <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight uppercase">Dunning Flow Designer</h1>
            <p className="text-[11px] font-black text-indigo-500 uppercase tracking-[0.2em] mt-1 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse"></span>
              Automated Retention Engine
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
           {showSaved && (
             <span className="text-[10px] font-black uppercase text-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 px-4 py-2 rounded-xl border border-emerald-100 dark:border-emerald-800 animate-in zoom-in-95">
               Policy Updated
             </span>
           )}
           {showUndo && (
             <button 
               onClick={undoDelete}
               className="text-[10px] font-black uppercase text-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 px-4 py-2 rounded-xl border border-indigo-100 dark:border-indigo-800 animate-in slide-in-from-right-4 hover:bg-indigo-100 transition-all flex items-center gap-2"
             >
               <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7v6h6"/><path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"/></svg>
               Undo Delete
             </button>
           )}
           <div className="bg-white dark:bg-slate-800 p-1.5 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm flex">
              <button 
                onClick={() => setActiveTab('dunning')}
                className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'dunning' ? 'bg-slate-900 dark:bg-slate-700 text-white shadow-lg' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'}`}
              >Dunning</button>
              <button 
                onClick={() => setActiveTab('cancellationSaver')}
                className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'cancellationSaver' ? 'bg-slate-900 dark:bg-slate-700 text-white shadow-lg' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'}`}
              >Cancellation Saver</button>
           </div>
        </div>
      </div>

      {activeTab === 'dunning' && (
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-10">
        
        {/* Visual Timeline Column */}
        <div className="xl:col-span-8 space-y-8">
           <div className="bg-white dark:bg-slate-800 rounded-[3rem] border border-slate-200 dark:border-slate-700 shadow-xl shadow-slate-100/50 dark:shadow-none overflow-hidden">
              <div className="p-8 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center bg-slate-50/30 dark:bg-slate-900/20">
                 <div>
                    <h3 className="text-lg font-black text-slate-900 dark:text-white uppercase tracking-tight">Sequence Visualization</h3>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">Define nodes of failure recovery</p>
                 </div>
                 <div className="flex gap-2">
                   <button 
                     onClick={() => setViewMode('timeline')}
                     className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase ${viewMode === 'timeline' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600'}`}
                   >Timeline</button>
                   <button 
                     onClick={() => setViewMode('flowchart')}
                     className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase ${viewMode === 'flowchart' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600'}`}
                   >Flowchart</button>
                   <button 
                     onClick={addStep}
                     className="px-6 py-3 bg-slate-900 dark:bg-indigo-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:scale-105 transition-all shadow-xl active:scale-95"
                   >
                     + Add Sequence Node
                   </button>
                 </div>
              </div>

              <div className="p-10 relative">
                {viewMode === 'flowchart' ? (
                  <DunningFlowchart steps={rule.steps} onStepsChange={(steps) => handleRuleUpdate({ steps })} onDeleteStep={deleteStep} />
                ) : (
                  <>
                    {/* The Logic Path Line */}
                    <div className="absolute left-[3.25rem] top-12 bottom-12 w-[2px] bg-slate-100 dark:bg-slate-700">
                       <div className="h-full w-full border-l-2 border-dashed border-slate-300 dark:border-slate-600"></div>
                    </div>

                    {rule.steps.length === 0 ? (
                      <div className="py-24 text-center border-4 border-dashed border-slate-50 dark:border-slate-700 rounded-[3rem] ml-12 bg-slate-50/20">
                         <div className="w-16 h-16 bg-white dark:bg-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm">
                           <svg className="text-slate-300" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20"/><path d="m17 5-5-3-5 3"/><path d="m17 19-5 3-5-3"/><path d="M2 12h20"/><path d="m5 7-3 5 3 5"/><path d="m19 7 3 5-3 5"/></svg>
                         </div>
                         <p className="text-slate-400 font-black text-sm uppercase tracking-widest">No nodes in recovery sequence</p>
                         <button onClick={addStep} className="text-indigo-600 font-black text-[11px] uppercase mt-4 hover:bg-indigo-50 px-6 py-2.5 rounded-full border border-indigo-100 transition-all">Launch Designer</button>
                      </div>
                    ) : (
                      <div className="space-y-0">
                        {rule.steps.map((step, index) => (
                         <div key={step.id} className="relative pl-20 pb-16 last:pb-8 group animate-in slide-in-from-left-4 duration-500" style={{ animationDelay: `${index * 100}ms` }}>
                             
                             {/* Timeline Node Icon */}
                             <div className="absolute left-10 top-0 w-8 h-8 rounded-xl bg-white dark:bg-slate-900 border-4 border-slate-100 dark:border-slate-700 z-10 flex items-center justify-center shadow-lg group-hover:scale-125 group-hover:border-indigo-600 transition-all duration-300">
                                <span className="text-[11px] font-black text-slate-400 group-hover:text-indigo-600">{index + 1}</span>
                             </div>

                             {/* Connector Pill */}
                             {index > 0 && (
                                <div className="absolute left-[2rem] -top-10 w-24 flex items-center justify-center pointer-events-none">
                                   <div className="px-3 py-1 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-full text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] shadow-sm">
                                     +{rule.steps[index-1].dayDelay} Days
                                   </div>
                                </div>
                             )}

                             <div className="bg-slate-50 dark:bg-slate-900/40 rounded-[2.5rem] border border-slate-200/60 dark:border-slate-700 p-8 hover:bg-white dark:hover:bg-slate-900 transition-all hover:shadow-2xl hover:shadow-indigo-500/5 hover:-translate-y-1">
                                <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                                   
                                   {/* Step Time Delay */}
                                   <div className="md:col-span-3 space-y-4">
                                      <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.15em] flex justify-between">
                                         Node Delay
                                      </label>
                                      <div className="relative group/input">
                                         <input 
                                           type="number" 
                                           value={step.dayDelay} 
                                           onChange={e => updateStep(step.id, { dayDelay: parseInt(e.target.value) || 0 })}
                                           className="w-full pl-6 pr-14 py-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl font-black text-3xl text-slate-900 dark:text-white outline-none focus:ring-8 focus:ring-indigo-600/5 transition-all shadow-inner" 
                                         />
                                         <span className="absolute right-6 top-1/2 -translate-y-1/2 text-[10px] font-black text-slate-300 uppercase">Days</span>
                                      </div>
                                      <div className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-slate-800 rounded-lg border border-slate-100 dark:border-slate-700">
                                         <div className="w-1.5 h-1.5 rounded-full bg-indigo-500"></div>
                                         <p className="text-[9px] text-slate-400 font-bold uppercase tracking-tighter">Wait after prev. node</p>
                                      </div>
                                   </div>

                                   {/* Communication Trigger */}
                                   <div className="md:col-span-4 space-y-4">
                                      <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.15em]">Communication Hub</label>
                                      <div className="relative group/select">
                                         <select 
                                           value={step.emailTemplateId} 
                                           onChange={e => updateStep(step.id, { emailTemplateId: e.target.value })}
                                           className="w-full px-5 py-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl font-bold text-xs text-slate-900 dark:text-white outline-none focus:ring-8 focus:ring-indigo-600/5 appearance-none shadow-sm cursor-pointer"
                                         >
                                             <option value="none">No Communication</option>
                                             {settings.emailTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                         </select>
                                         <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-300">
                                           <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
                                         </div>
                                      </div>
                                      <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium px-1">Trigger transactional recovery email to customer.</p>
                                   </div>

                                   {/* System Action Trigger */}
                                   <div className="md:col-span-5 space-y-4">
                                      <div className="flex justify-between items-center">
                                         <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.15em]">System Protocol</label>
                                         <button onClick={() => deleteStep(step.id)} className="p-2 text-slate-300 hover:text-red-500 transition-all opacity-0 group-hover:opacity-100 hover:bg-red-50 rounded-xl">
                                           <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                                         </button>
                                      </div>
                                      <div className="relative group/select">
                                         <select 
                                           value={step.action} 
                                           onChange={e => updateStep(step.id, { action: e.target.value as any })}
                                           className={`w-full px-12 py-4 border-2 rounded-2xl font-bold text-xs outline-none focus:ring-8 transition-all appearance-none cursor-pointer ${getActionColor(step.action)}`}
                                           style={{ '--tw-ring-color': 'rgba(79, 70, 229, 0.05)' } as any}
                                         >
                                             <option value="retry">Execute Smart Retry</option>
                                             <option value="mark_past_due">Shift to Past Due</option>
                                             <option value="cancel">Hard Terminate</option>
                                         </select>
                                         <div className="absolute left-5 top-1/2 -translate-y-1/2 pointer-events-none opacity-60">
                                            {getActionIcon(step.action)}
                                         </div>
                                         <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none opacity-40">
                                           <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
                                         </div>
                                      </div>
                                      <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium px-1 italic">Handshake performed with payment gateway API.</p>
                                   </div>

                                </div>
                             </div>
                         </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                 {/* The Terminal Node - Sequence Termination */}
                 <div className="relative pl-20 mt-4 group">
                    <div className="absolute left-10 top-2 w-8 h-8 rounded-full bg-slate-900 border-4 border-white dark:border-slate-800 z-10 flex items-center justify-center text-white shadow-2xl transition-transform group-hover:scale-110">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17 4 12"/></svg>
                    </div>
                    <div className="bg-slate-900 rounded-[3rem] p-10 text-white border-4 border-slate-100 dark:border-slate-700 shadow-2xl relative overflow-hidden group-hover:border-indigo-500/50 transition-colors">
                       <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-10 relative z-10">
                          <div>
                             <h4 className="text-sm font-black uppercase tracking-[0.25em] text-indigo-400">Flow Termination</h4>
                             <p className="text-xs text-slate-400 mt-2 font-medium">Final outcome after <span className="text-white font-bold">{rule.steps.length} nodes</span> and <span className="text-white font-bold">{totalDunningDays} days</span>.</p>
                          </div>
                          <div className="text-right px-6 py-3 bg-white/5 rounded-2xl border border-white/5">
                             <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Final Action</p>
                             <span className="text-2xl font-black text-red-500 uppercase tracking-tighter">Auto-Purge</span>
                          </div>
                       </div>
                       
                       <div className="bg-indigo-600/10 p-6 rounded-[1.5rem] border border-indigo-500/20 relative z-10 flex gap-6 items-center">
                          <div className="w-16 h-16 bg-red-500/20 rounded-2xl flex items-center justify-center text-red-500 shrink-0 shadow-inner">
                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                          </div>
                          <p className="text-xs text-slate-300 leading-relaxed font-bold">
                            If all recovery attempts are exhausted without success, the customer account is moved to <span className="text-white underline decoration-red-500 decoration-2 underline-offset-4">Canceled</span> status. SubScale will instantly revoke platform access, purge JWT tokens, and broadcast the <span className="font-mono text-[10px] bg-slate-800 px-1.5 py-0.5 rounded">subscription.deleted</span> event to your configured webhooks.
                          </p>
                       </div>

                       {/* Background abstract decoration */}
                       <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-indigo-500/5 rounded-full blur-[100px] pointer-events-none"></div>
                       <div className="absolute top-0 right-0 p-10 opacity-10 pointer-events-none">
                          <svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7 a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                       </div>
                    </div>
                 </div>
              </div>
           </div>
        </div>

        {/* Global Policy Settings Column */}
        <div className="xl:col-span-4 space-y-10">
           
           {/* Continuity Policy */}
           <div className="bg-white dark:bg-slate-800 rounded-[3rem] p-10 border border-slate-200 dark:border-slate-700 shadow-sm relative overflow-hidden group">
              <div className="flex items-center gap-3 mb-10">
                <div className="w-10 h-10 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                   <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20"/><path d="m17 5-5-3-5 3"/><path d="m17 19-5 3-5-3"/><path d="M2 12h20"/><path d="m5 7-3 5 3 5"/><path d="m19 7 3 5-3 5"/></svg>
                </div>
                <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest">Global Policy</h3>
              </div>

              <div className="space-y-8">
                 <div className="space-y-4">
                    <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1 flex justify-between">
                       Grace Period
                       <span className="text-indigo-600 font-black">{rule.gracePeriodDays} Days</span>
                    </label>
                    <div className="relative">
                       <input 
                         type="number" 
                         value={rule.gracePeriodDays} 
                         onChange={e => handleRuleUpdate({ gracePeriodDays: parseInt(e.target.value) || 0 })}
                         className="w-full px-8 py-6 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-[2.5rem] font-black text-4xl text-indigo-600 outline-none focus:ring-8 focus:ring-indigo-600/5 transition-all shadow-inner" 
                       />
                       <span className="absolute right-8 top-1/2 -translate-y-1/2 text-xs font-black text-slate-400 uppercase tracking-widest">Days</span>
                    </div>
                 </div>
                 <div className="space-y-4">
                    <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1 flex justify-between">
                       Max Dunning Duration
                       <span className="text-rose-600 font-black">{rule.maxDunningDays} Days</span>
                    </label>
                    <div className="relative">
                       <input 
                         type="number" 
                         value={rule.maxDunningDays} 
                         onChange={e => handleRuleUpdate({ maxDunningDays: parseInt(e.target.value) || 0 })}
                         className="w-full px-8 py-6 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-[2.5rem] font-black text-4xl text-rose-600 outline-none focus:ring-8 focus:ring-rose-600/5 transition-all shadow-inner" 
                       />
                       <span className="absolute right-8 top-1/2 -translate-y-1/2 text-xs font-black text-slate-400 uppercase tracking-widest">Days</span>
                    </div>
                 </div>
                 <div className="bg-indigo-50 dark:bg-indigo-900/20 p-6 rounded-2xl border border-indigo-100 dark:border-indigo-800">
                   <p className="text-[11px] text-indigo-900 dark:text-indigo-300 leading-relaxed font-bold">
                     The "Full Service Window" during which customers retain 100% feature access despite a pending payment failure. High values reduce churn risk but increase bad debt.
                   </p>
                 </div>
              </div>
              
              <div className="absolute -bottom-10 -right-10 w-48 h-48 bg-indigo-500/5 rounded-full blur-3xl group-hover:bg-indigo-500/10 transition-all duration-1000 pointer-events-none"></div>
           </div>

           {/* Health Indicators */}
           <div className="bg-indigo-900 rounded-[3rem] p-10 text-white relative overflow-hidden shadow-2xl group">
              <div className="flex justify-between items-start mb-12">
                <h3 className="text-sm font-black uppercase tracking-widest opacity-60">Engine Diagnostics</h3>
                <div className="px-3 py-1 bg-white/10 rounded-lg text-[9px] font-black uppercase tracking-widest border border-white/5 backdrop-blur-sm">System Healthy</div>
              </div>
              <div className="space-y-12 relative z-10">
                 <div className="flex justify-between items-end">
                    <div>
                       <p className="text-[10px] font-black text-indigo-300 uppercase tracking-widest mb-2">Net Recovery Rate</p>
                       <p className="text-5xl font-black tracking-tighter">84.2%</p>
                    </div>
                    <div className="text-right">
                       <p className="text-[10px] font-black text-indigo-300 uppercase tracking-widest mb-2">Saved Revenue</p>
                       <p className="text-2xl font-black text-emerald-400 tracking-tight">+$12,450</p>
                    </div>
                 </div>

                 <div className="space-y-6">
                    {[
                      { label: 'Immediate Recovery', val: 68 },
                      { label: 'Secondary Node', val: 12 },
                      { label: 'Tertiary Threshold', val: 4.2 },
                    ].map((s, i) => (
                      <div key={i} className="space-y-2">
                         <div className="flex justify-between text-[10px] font-black uppercase tracking-[0.15em] opacity-80">
                            <span>{s.label}</span>
                            <span className="text-indigo-200">{s.val}% Success</span>
                         </div>
                         <div className="w-full h-2.5 bg-white/10 rounded-full overflow-hidden shadow-inner">
                            <div className="h-full bg-white transition-all duration-1000 ease-out shadow-[0_0_15px_rgba(255,255,255,0.5)]" style={{ width: `${(s.val/84.2)*100}%` }}></div>
                         </div>
                      </div>
                    ))}
                 </div>
              </div>
              
              <div className="absolute -bottom-10 -right-10 w-64 h-64 bg-white/5 rounded-full blur-[100px] pointer-events-none group-hover:scale-125 transition-transform duration-1000"></div>
           </div>

           {/* Policy Audit Mini-Log */}
           <div className="bg-white dark:bg-slate-800 rounded-[3rem] p-10 border border-slate-200 dark:border-slate-700 shadow-sm group overflow-hidden">
              <div className="flex justify-between items-center mb-8">
                 <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest">Protocol Audit</h3>
                 <button className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest hover:underline decoration-2 underline-offset-4">Full Log</button>
              </div>
              <div className="space-y-8 relative">
                 <div className="absolute left-4 top-2 bottom-2 w-px bg-slate-50 dark:bg-slate-700"></div>
                 {[
                   { user: 'Acme Corp', action: 'Handshake Success', time: '14m ago', status: 'Success' },
                   { user: 'SkyNet Systems', action: 'Retry Node #2 Fired', time: '2h ago', status: 'Pending' },
                   { user: 'Global Logistics', action: 'Policy Purge', time: '1d ago', status: 'Canceled' },
                 ].map((log, i) => (
                   <div key={i} className="flex justify-between items-start pl-10 relative group/log">
                      <div className="absolute left-3 top-1.5 w-2 h-2 rounded-full bg-slate-200 dark:bg-slate-600 group-hover/log:bg-indigo-500 transition-colors"></div>
                      <div>
                         <p className="text-sm font-black text-slate-900 dark:text-white group-hover/log:text-indigo-600 transition-colors">{log.user}</p>
                         <p className="text-[10px] text-slate-500 dark:text-slate-400 font-bold uppercase mt-0.5">{log.action}</p>
                      </div>
                      <div className="text-right">
                         <p className="text-[9px] text-slate-400 font-black uppercase tracking-widest">{log.time}</p>
                         <p className={`text-[9px] font-black uppercase mt-1 ${log.status === 'Success' ? 'text-emerald-500' : log.status === 'Pending' ? 'text-amber-500' : 'text-red-500'}`}>{log.status}</p>
                      </div>
                   </div>
                 ))}
              </div>
           </div>
        </div>
      </div>
      )}
      {activeTab === 'cancellationSaver' && (
        <div className="max-w-2xl mx-auto">
          <CancellationSaver rule={cancellationSaverRule} onUpdate={handleCancellationSaverUpdate} />
        </div>
      )}
    </div>
  );
};

export default DunningManagement;
